import React, {CSSProperties, ReactNode, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCopyright, faMinus, faPlus} from "@fortawesome/free-solid-svg-icons";
import './family-card.scss';

const DEFAULT_BIRD_ICON = '/assets/fallback-bird.svg';

export type FamilyCardProps = {
    onClick?: (subject: any) => void
    subject: any;
    seen?: boolean;
    statusLabel?: string;
    statusVariant?: 'default'|'extinct';
    img?: string;
    alt?: string;
    title?: string | ReactNode;
    subtitle?: string | ReactNode;
    description?: string | ReactNode;
    statProgress?: number;
    statCount?: string | ReactNode;
    attributionUrl?: string;
    attributionLabel?: string;
    imageOnly?: boolean;
}

export function FamilyExplorerCard({subject, onClick, seen = false, statusLabel, statusVariant = 'default', img, alt = '', title, subtitle, description, statProgress, statCount, attributionUrl, attributionLabel, imageOnly = false}: FamilyCardProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const hasDetails = !imageOnly && !!description && !onClick && seen;
    const speciesCode = subject?.speciesCode;
    const speciesUrl = !imageOnly && speciesCode ? `https://ebird.org/species/${speciesCode}` : undefined;
    const isFallbackImage = !img || img.endsWith(DEFAULT_BIRD_ICON);
    const resolvedStatusLabel = statusLabel ?? (seen ? 'Seen' : 'Not Seen');
    const statusClassName = [
        'family-card__status',
        statusVariant === 'extinct' ? 'family-card__status--extinct' : ''
    ].filter(Boolean).join(' ');

    const getClassName = () => {
        const classes = ['family-card'];
        classes.push(seen ? 'family-card--seen' : 'family-card--unseen');
        if (onClick) classes.push('family-card--clickable');
        if (hasDetails) classes.push('family-card--has-details');
        if (isExpanded) classes.push('family-card--expanded');
        if (imageOnly) classes.push('family-card--image-only');

        return classes.join(' ');
    }

    const handleClick = () => {
        if (onClick) {
            onClick(subject);
            return;
        }

        setIsExpanded(!isExpanded);
    }

    const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (event.currentTarget.src.endsWith(DEFAULT_BIRD_ICON)) return;

        event.currentTarget.src = DEFAULT_BIRD_ICON;
        event.currentTarget.classList.add('family-card__img--fallback');
    }

    const getProgressStyle = (): CSSProperties & {'--family-card-stat-progress'?: string} => {
        const progress = Math.max(0, Math.min(statProgress ?? 0, 1));
        return {'--family-card-stat-progress': `${progress * 100}%`};
    }

    const progressPercent = Math.round(Math.max(0, Math.min(statProgress ?? 0, 1)) * 100);

    const cardContent = (
        <>
            <span className="family-card__img-wrap">
                <img
                    src={img || DEFAULT_BIRD_ICON}
                    alt={alt}
                    className={isFallbackImage ? 'family-card__img family-card__img--fallback' : 'family-card__img'}
                    onError={handleImageError}
                />
            </span>
            {!imageOnly && hasDetails ?
                <button
                    type="button"
                    className="family-card__status family-card__status--toggle"
                    onClick={handleClick}
                    aria-expanded={isExpanded}
                >
                    Seen <span className="family-card__status-icon" aria-hidden="true">
                        <FontAwesomeIcon icon={isExpanded ? faMinus : faPlus} />
                    </span>
                </button> :
                !imageOnly ? <span className={statusClassName}>{resolvedStatusLabel}</span> : null
            }
            {!imageOnly ? (
                <div className="family-card__content">
                    <div className="family-card__title">{title}</div>
                    {subtitle && <div className="family-card__subtitle">{subtitle}</div>}
                    {statProgress !== undefined && <div className="family-card__stats">
                        <div className="family-card__stats-row">
                            <span className="family-card__stat-count">{statCount ?? subtitle}</span>
                            <strong>{progressPercent}%</strong>
                        </div>
                        <div
                            className="family-card__progress"
                            style={getProgressStyle()}
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={progressPercent}
                        >
                            <span />
                        </div>
                    </div>}
                    {speciesUrl && <div className="family-card__source-links">
                        {speciesUrl && <a
                            className="family-card__source-link"
                            href={speciesUrl}
                            target="_blank"
                            rel="noreferrer"
                        >eBird</a>}
                    </div>}
                </div>
            ) : null}
            {attributionUrl && <a
                className="family-card__attribution-link"
                href={attributionUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={attributionLabel ? `Image attribution: ${attributionLabel}` : 'Image attribution'}
                title={attributionLabel ? `Image attribution: ${attributionLabel}` : 'Image attribution'}
            >
                <FontAwesomeIcon icon={faCopyright} aria-hidden="true" />
                {attributionLabel ? <span>{attributionLabel}</span> : null}
            </a>}
            {hasDetails && <div className="family-card__desc" aria-hidden={!isExpanded}>
                <span className="family-card__desc-text">{description}</span>
            </div>}
        </>
    );

    return (
        <div className={getClassName()}>
            {onClick ?
                <button type="button" className="family-card__link" onClick={handleClick}>
                    {cardContent}
                </button> :
                <div className="family-card__link">
                    {cardContent}
                </div>
            }
        </div>
    )
}
